import React, { JSX, useEffect, useMemo, useRef, useState } from 'react'
import { graphqlRequest } from 'lib/helpers'
import { connectTwitchChat } from 'lib/twitchChat'
import { ChatMessage } from 'lib/twitchChatParse'
import {
  EmoteCatalog,
  EmoteLookup,
  EmoteStore,
  createEmoteStore,
  extractEmotes
} from 'lib/thirdPartyEmotes'
import { buildAppSyncWsClient, WEBSOCKET_SUB_QUERY } from 'lib/appSyncSubscription'
import {
  ChunkMemory,
  WALL_EMOJI_FONT_FAMILY,
  createChunkMemory,
  enqueueWallEmoji,
  spreadGapMs,
  wallEmojiGroupsOf,
  wallTagOf,
  youtubeListOf
} from 'lib/wallEmoji'
import { compileBlockedWords, messageIsBlocked } from 'lib/chatModeration'
import { PLATFORM_MARK_STYLE, YOUTUBE_MARK_SRC } from 'components/chatOverlays/platformMarks'
import { OverlayPreviewSeam, previewSeamActive } from 'components/overlayPreview/previewSeam'
import { EmoteWallPreviewConfig, isYouTubeWallConfig } from 'components/overlayPreview/previewConfig'
import {
  WallPreviewFeed,
  sampleTagStyle,
  useDocumentVisible,
  usePrefersReducedMotion,
  useWallFeedReal,
  wallSampleTagBasePx
} from 'components/overlayPreview/previewFeeds'
import {
  SAMPLE_OPACITY,
  WALL_STATIC_SPRITES,
  WallSampleEmote,
  elementSeed,
  wallSampleInterval,
  wallSampleKeyOf,
  wallSamplePool,
  wallSampleSlot,
  wallSlotsBetween
} from 'lib/overlaySamples'
import getEmoteWallDataQuery from 'graphql/getEmoteWallDataById.graphql'
import logger from 'logger'

const log = logger.extend('emote-wall')

const BOOTSTRAP_RETRY_MS = 15000
const EMPTY_CHANNELS_RETRY_MS = 60000
export const WALL_SETTINGS_REREAD_MS = 60000
export const blockedListKeyOf = (words: readonly string[]): string => JSON.stringify(words)

export const startWallListReread = ({
  readList,
  seqOf,
  currentKeyOf,
  apply,
  onError,
  intervalMs = WALL_SETTINGS_REREAD_MS
}: {
  readList: () => Promise<string[] | null>
  seqOf: () => number
  currentKeyOf: () => string
  apply: (words: string[], key: string) => void
  onError: (err: unknown) => void
  intervalMs?: number
}): (() => void) => {
  let stopped = false
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined
  const reread = (): void => {
    const seq = seqOf()
    readList()
      .then((words) => {
        if (stopped || words == null || seq !== seqOf()) return
        const key = blockedListKeyOf(words)
        if (key !== currentKeyOf()) apply(words, key)
      })
      .catch((err: unknown) => {
        if (!stopped) onError(err)
      })
      .finally(() => {
        if (!stopped) timer = globalThis.setTimeout(reread, intervalMs)
      })
  }
  timer = globalThis.setTimeout(reread, intervalMs)
  return () => {
    stopped = true
    globalThis.clearTimeout(timer)
  }
}
const MAX_EMOTES_PER_MESSAGE = 10
const SWEEP_INTERVAL_MS = 1000

interface ApiWallChannel {
  channelLogin: string
  broadcasterUserId: string
}

interface EmoteWallConfig {
  emoteWallId: string
  name: string
  style: string
  emoteSize: number
  density: number
  durationSeconds: number
  showTwitchEmotes: boolean
  show7tvEmotes: boolean
  showBttvEmotes: boolean
  showFfzEmotes: boolean
  youtubeChatChannels?: string[] | null
  blockedWords?: string[] | null
}

type WallRenderConfig = Pick<EmoteWallConfig, 'style' | 'emoteSize' | 'density' | 'durationSeconds' | 'showTwitchEmotes' | 'show7tvEmotes' | 'showBttvEmotes' | 'showFfzEmotes'> & {
  youtubeChatChannels?: string[] | null
  source?: 'TWITCH' | 'YOUTUBE'
  blockedWords?: string[] | null
}

interface EmoteWallData {
  channels: ApiWallChannel[]
  config: EmoteWallConfig
  youtubeEmojiChannel?: string | null
}

interface WallBootstrap {
  channels: string[]
  broadcasterUserIds: string[]
  config: EmoteWallConfig
  youtubeChannels: string[]
  youtubeEmojiChannel: string | null
}

const foldBootstrap = (data: EmoteWallData): WallBootstrap => ({
  channels: data.channels.map((c) => c.channelLogin),
  broadcasterUserIds: data.channels.map((c) => c.broadcasterUserId).filter((id) => id !== ''),
  config: data.config,
  youtubeChannels: youtubeListOf(data.config.youtubeChatChannels),
  youtubeEmojiChannel: typeof data.youtubeEmojiChannel === 'string' && data.youtubeEmojiChannel !== '' ? data.youtubeEmojiChannel : null
})

interface Sprite {
  key: number
  url: string
  alt: string
  emoji?: string
  leftPct: number
  topPct: number
  driftPx: number
  heightPx: number
  spawnedAt: number
}

const randomFloat = (): number => {
  const buf = new Uint32Array(1)
  crypto.getRandomValues(buf)
  return buf[0] / 2 ** 32
}

const KEYFRAMES_CSS = `
@keyframes ewRain {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(var(--ew-drift, 0px), var(--ew-travel, 110vh), 0); }
}
@keyframes ewPop {
  0% { transform: scale(0); opacity: 0; }
  12% { transform: scale(1.12); opacity: 1; }
  22% { transform: scale(1); opacity: 1; }
  80% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.9); opacity: 0; }
}
`

const CONTAINER_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  pointerEvents: 'none'
}

const FILL_HOST_CONTAINER_STYLE: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  pointerEvents: 'none'
}

const spriteStyle = (sprite: Sprite, style: string, durationSeconds: number, hostHeightPx: number | null, delayS?: number): React.CSSProperties => {
  const delay = delayS == null ? '' : ` ${delayS}s`
  const base: React.CSSProperties = {
    position: 'absolute',
    left: `${sprite.leftPct}%`,
    height: `${sprite.heightPx}px`
  }
  if (style === 'POP') {
    return {
      ...base,
      top: `${sprite.topPct}%`,
      animation: `ewPop ${durationSeconds}s ease-in-out${delay} forwards`
    }
  }
  return Object.assign(
    {},
    base,
    {
      top: `${-sprite.heightPx * 2}px`,
      animation: `ewRain ${durationSeconds}s linear${delay} forwards`
    },
    {
      '--ew-drift': `${sprite.driftPx}px`,
      '--ew-travel': hostHeightPx !== null
        ? `${hostHeightPx + sprite.heightPx * 4}px`
        : `calc(100vh + ${sprite.heightPx * 4}px)`
    }
  )
}

const emojiSpriteStyle = (base: React.CSSProperties, heightPx: number): React.CSSProperties => ({
  ...base,
  display: 'block',
  lineHeight: `${heightPx}px`,
  fontSize: `${Math.max(1, Math.round(heightPx * 0.88))}px`,
  fontFamily: WALL_EMOJI_FONT_FAMILY,
  whiteSpace: 'nowrap',
  userSelect: 'none'
})

const YOUTUBE_MARK_WRAP_STYLE: React.CSSProperties = {
  position: 'absolute',
  right: 12,
  bottom: 12,
  lineHeight: 0,
  pointerEvents: 'none'
}

interface YouTubeLane {
  queue: string[][]
  gapMs: number
  timer: ReturnType<typeof globalThis.setTimeout> | undefined
  chunks: ChunkMemory
}

const stopYouTubeLane = (lane: YouTubeLane | null): void => {
  if (lane == null) return
  globalThis.clearTimeout(lane.timer)
  lane.timer = undefined
  lane.queue = []
}

const YouTubeWallMark = (): JSX.Element => (
  <span data-testid='emote-wall-youtube-mark' style={YOUTUBE_MARK_WRAP_STYLE}>
    <img src={YOUTUBE_MARK_SRC} alt='YouTube' title='YouTube' style={PLATFORM_MARK_STYLE} />
  </span>
)

interface SampleSprite {
  key: string
  url: string
  emoji?: string
  provider: WallSampleEmote['provider']
  leftPct: number
  topPct: number
  driftPx: number
  sizeFactor: number
  spawnedAt: number
}

const EMPTY_SAMPLES: SampleSprite[] = []
const EMPTY_POOL: WallSampleEmote[] = []
const SAMPLE_EXPIRY_GRACE_MS = 500

const SAMPLE_LAYER_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  opacity: SAMPLE_OPACITY
}

const sampleHeightPx = (emoteSize: number, sizeFactor: number): number =>
  Math.max(1, Math.round((Number.isFinite(emoteSize) ? emoteSize : 0) * sizeFactor))

const SampleSpriteImg = ({
  sprite,
  style,
  durationSeconds,
  emoteSize,
  hostHeightPx
}: {
  sprite: SampleSprite
  style: string
  durationSeconds: number
  emoteSize: number
  hostHeightPx: number | null
}): JSX.Element => {
  const [delayS] = useState(() => Math.min(0, Math.round(sprite.spawnedAt - Date.now()) / 1000))
  const shape: Sprite = {
    key: 0,
    url: sprite.url,
    alt: '',
    leftPct: sprite.leftPct,
    topPct: sprite.topPct,
    driftPx: sprite.driftPx,
    heightPx: sampleHeightPx(emoteSize, sprite.sizeFactor),
    spawnedAt: sprite.spawnedAt
  }
  const placed = spriteStyle(shape, style, durationSeconds, hostHeightPx, delayS)
  if (sprite.emoji != null) {
    return <span aria-hidden='true' style={emojiSpriteStyle(placed, shape.heightPx)}>{sprite.emoji}</span>
  }
  return <img src={sprite.url} alt='' style={placed} />
}

const staticSampleStyle = (slot: { leftPct: number, topPct: number, sizeFactor: number }, emoteSize: number): React.CSSProperties => ({
  position: 'absolute',
  left: `${slot.leftPct}%`,
  top: `${slot.topPct}%`,
  height: `${sampleHeightPx(emoteSize, slot.sizeFactor)}px`,
  animation: 'none'
})

export const EmoteWallView = ({
  emoteWallId,
  token,
  slug,
  fillHost = false,
  preview = null,
  previewConfig = null
}: {
  emoteWallId: string
  token: string
  slug?: string
  fillHost?: boolean
  preview?: OverlayPreviewSeam | null
  previewConfig?: EmoteWallPreviewConfig | null
}): JSX.Element => {
  const [bootstrap, setBootstrap] = useState<WallBootstrap | null>(null)
  const [sprites, setSprites] = useState<Sprite[]>([])
  const previewOn = previewSeamActive(preview)
  const mockup = previewOn && preview.mode === 'mockup'
  const wallFeed: WallPreviewFeed | null = previewOn ? preview.wallFeed : null
  const feedKey = emoteWallId === '' ? 'mockup' : emoteWallId
  const previewOnRef = useRef(false)
  previewOnRef.current = previewOn
  const wallConfig: WallRenderConfig | null = bootstrap?.config ?? (previewOn ? previewConfig ?? null : null)
  const youtubeWall = isYouTubeWallConfig(wallConfig)
  const lookupRef = useRef<EmoteLookup | null>(null)
  const emoteStoreRef = useRef<EmoteStore | null>(null)
  const spriteKeyRef = useRef(0)

  const hostRef = useRef<HTMLDivElement | null>(null)
  const [hostHeight, setHostHeight] = useState<number | null>(null)
  useEffect(() => {
    if (!fillHost) return
    const node = hostRef.current
    if (node === null) return
    setHostHeight(node.offsetHeight)
    const ro = new ResizeObserver(() => setHostHeight(node.offsetHeight))
    ro.observe(node)
    return () => ro.disconnect()
  }, [fillHost])

  useEffect(() => {
    if (emoteWallId === '' || token === '') return
    if (mockup) return
    let cancelled = false
    let retryTimer: ReturnType<typeof globalThis.setTimeout> | undefined
    const load = (): void => {
      graphqlRequest<{ emoteWall: EmoteWallData | null }>(
        getEmoteWallDataQuery,
        slug !== undefined ? { emoteWallId, slug } : { emoteWallId },
        token
      )
        .then((res) => {
          if (cancelled) return
          if (res.emoteWall === null) return
          const folded = foldBootstrap(res.emoteWall)
          setBootstrap(folded)
          if (folded.channels.length === 0 && folded.youtubeChannels.length === 0) {
            retryTimer = globalThis.setTimeout(load, EMPTY_CHANNELS_RETRY_MS)
          }
        })
        .catch((err: unknown) => {
          log({ bootstrap: 'failed', err: err instanceof Error ? err.name : 'unknown' })
          if (!cancelled) retryTimer = globalThis.setTimeout(load, BOOTSTRAP_RETRY_MS)
        })
    }
    load()
    return () => {
      cancelled = true
      globalThis.clearTimeout(retryTimer)
    }
  }, [emoteWallId, token, slug, mockup])

  const idsKey = bootstrap === null ? null : bootstrap.broadcasterUserIds.join(' ')
  const twitchStoreIdsKey = previewOn && wallConfig != null ? (idsKey ?? '') : idsKey
  const storeIdsKey = youtubeWall ? null : twitchStoreIdsKey
  const show7tv = wallConfig?.show7tvEmotes ?? true
  const showBttv = wallConfig?.showBttvEmotes ?? true
  const showFfz = wallConfig?.showFfzEmotes ?? true
  const [catalog, setCatalog] = useState<EmoteCatalog | null>(null)
  const lastProvidersKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (storeIdsKey == null) return
    const ids = storeIdsKey === '' ? [] : storeIdsKey.split(' ')
    const providersKey = `${String(show7tv)} ${String(showBttv)} ${String(showFfz)}`
    const freshCheck = lastProvidersKeyRef.current !== null && lastProvidersKeyRef.current !== providersKey
    lastProvidersKeyRef.current = providersKey
    const store = previewOn
      ? createEmoteStore(
        ids,
        (lookup) => { lookupRef.current = lookup },
        { providers: { sevenTv: show7tv, bttv: showBttv, ffz: showFfz }, freshCheck, onCatalog: (next) => setCatalog(next) }
      )
      : createEmoteStore(
        ids,
        (lookup) => { lookupRef.current = lookup },
        { providers: { sevenTv: show7tv, bttv: showBttv, ffz: showFfz }, freshCheck }
      )
    emoteStoreRef.current = store
    return () => {
      emoteStoreRef.current = null
      lookupRef.current = null
      store.dispose()
    }
  }, [storeIdsKey, show7tv, showBttv, showFfz, previewOn])

  const [localRealLatch, setLocalRealLatch] = useState(false)
  const feedReal = useWallFeedReal(wallFeed, feedKey)
  const realLatched = localRealLatch || feedReal
  const onRealSpawnRef = useRef<() => void>(() => {})
  onRealSpawnRef.current = () => {
    setLocalRealLatch(true)
    if (wallFeed != null) wallFeed.markReal(feedKey)
  }
  useEffect(() => {
    if (wallFeed == null) return
    return wallFeed.hold(feedKey)
  }, [wallFeed, feedKey])

  const blockedRef = useRef(compileBlockedWords([]))
  const blockedKeyRef = useRef(blockedListKeyOf([]))
  const blockedSeqRef = useRef(0)
  const settingsRereadOn = bootstrap != null && bootstrap.channels.length > 0 && bootstrap.youtubeChannels.length === 0
  useEffect(() => {
    if (!settingsRereadOn || emoteWallId === '' || token === '' || mockup) return
    return startWallListReread({
      readList: async () => {
        const res = await graphqlRequest<{ emoteWall: EmoteWallData | null }>(
          getEmoteWallDataQuery,
          slug != null ? { emoteWallId, slug } : { emoteWallId },
          token
        )
        return res.emoteWall == null ? null : youtubeListOf(res.emoteWall.config.blockedWords)
      },
      seqOf: () => blockedSeqRef.current,
      currentKeyOf: () => blockedKeyRef.current,
      apply: (words, key) => {
        blockedKeyRef.current = key
        blockedRef.current = compileBlockedWords(words)
        log({ blockedWords: 'updated', words: words.length })
      },
      onError: (err) => {
        log({ settingsReread: 'failed', err: err instanceof Error ? err.name : 'unknown' })
      }
    })
  }, [settingsRereadOn, emoteWallId, token, slug, mockup])

  useEffect(() => {
    if (bootstrap === null || bootstrap.channels.length === 0) return
    const { config } = bootstrap
    const words = youtubeListOf(config.blockedWords)
    blockedSeqRef.current += 1
    blockedKeyRef.current = blockedListKeyOf(words)
    blockedRef.current = compileBlockedWords(words)
    const handlers = {
      onMessage: (message: ChatMessage) => {
        if (messageIsBlocked(blockedRef.current, message)) return
        const emotes = extractEmotes(message, lookupRef.current, {
          includeNative: config.showTwitchEmotes
        })
        if (emotes.length === 0) return
        if (previewOnRef.current) onRealSpawnRef.current()
        const now = Date.now()
        const spawned = emotes.slice(0, MAX_EMOTES_PER_MESSAGE).map((emote): Sprite => {
          spriteKeyRef.current += 1
          return {
            key: spriteKeyRef.current,
            url: emote.url,
            alt: emote.alt,
            leftPct: 2 + randomFloat() * 92,
            topPct: 8 + randomFloat() * 76,
            driftPx: Math.round((randomFloat() * 2 - 1) * 140),
            heightPx: Math.round(config.emoteSize * (0.75 + randomFloat() * 0.5)),
            spawnedAt: now
          }
        })
        setSprites((prev) => {
          const room = config.density - prev.length
          if (room <= 0) return prev
          return [...prev, ...spawned.slice(0, room)]
        })
      },
      onMessageDeleted: () => {},
      onChatCleared: () => {},
      onConnected: () => emoteStoreRef.current?.refreshNow()
    }
    const connection = connectTwitchChat(bootstrap.channels, handlers)
    return connection.close
  }, [bootstrap])

  const youtubeEmojiChannel = bootstrap == null ? null : bootstrap.youtubeEmojiChannel
  const youtubeChannelsKey = bootstrap == null ? '' : bootstrap.youtubeChannels.join(' ')
  const bootstrapConfigRef = useRef<EmoteWallConfig | null>(null)
  bootstrapConfigRef.current = bootstrap == null ? null : bootstrap.config
  const tagSourceId = bootstrap == null ? null : (typeof bootstrap.config.emoteWallId === 'string' && bootstrap.config.emoteWallId !== '' ? bootstrap.config.emoteWallId : emoteWallId)
  const wallTagRef = useRef<string | null>(null)
  useEffect(() => {
    if (previewOn || youtubeEmojiChannel == null || tagSourceId == null || tagSourceId === '') return
    let cancelled = false
    void wallTagOf(tagSourceId).then((tag) => {
      if (!cancelled) wallTagRef.current = tag
    })
    return () => {
      cancelled = true
      wallTagRef.current = null
    }
  }, [previewOn, youtubeEmojiChannel, tagSourceId])
  const [youtubeMarkOn, setYoutubeMarkOn] = useState(false)
  const youtubeLaneRef = useRef<YouTubeLane | null>(null)
  useEffect(() => () => stopYouTubeLane(youtubeLaneRef.current), [])
  useEffect(() => {
    if (previewOn) return
    if (youtubeEmojiChannel == null || token === '' || youtubeChannelsKey === '') {
      stopYouTubeLane(youtubeLaneRef.current)
      return
    }
    const accepted = youtubeChannelsKey.split(' ')
    if (youtubeLaneRef.current == null) {
      youtubeLaneRef.current = { queue: [], gapMs: spreadGapMs(0), timer: undefined, chunks: createChunkMemory() }
    }
    const lane = youtubeLaneRef.current
    const spawnGroup = (group: string[]): void => {
      const config = bootstrapConfigRef.current
      if (config == null) return
      const now = Date.now()
      const spawned = group.map((emoji): Sprite => {
        spriteKeyRef.current += 1
        return {
          key: spriteKeyRef.current,
          url: '',
          alt: '',
          emoji,
          leftPct: 2 + randomFloat() * 92,
          topPct: 8 + randomFloat() * 76,
          driftPx: Math.round((randomFloat() * 2 - 1) * 140),
          heightPx: Math.round(config.emoteSize * (0.75 + randomFloat() * 0.5)),
          spawnedAt: now
        }
      })
      setYoutubeMarkOn(true)
      setSprites((prev) => {
        const room = config.density - prev.length
        if (room <= 0) return prev
        return [...prev, ...spawned.slice(0, room)]
      })
    }
    const pump = (): void => {
      const next = lane.queue.shift()
      if (next == null) {
        lane.timer = undefined
        return
      }
      spawnGroup(next)
      lane.timer = globalThis.setTimeout(pump, lane.gapMs)
    }
    const client = buildAppSyncWsClient(token, { persistent: true })
    const unsubscribe = client.subscribe(
      { query: WEBSOCKET_SUB_QUERY, variables: { channel: youtubeEmojiChannel } },
      {
        next: (data: any) => {
          const dataField = data?.WebsocketSub?.data
          if (dataField == null) return
          let envelope: unknown
          try {
            envelope = typeof dataField === 'string' ? JSON.parse(dataField) : dataField
          } catch (err) {
            log({ ytLane: 'envelope parse failed', err: err instanceof Error ? err.name : 'unknown' })
            return
          }
          const address = {
            tag: wallTagRef.current,
            listEmpty: youtubeListOf(bootstrapConfigRef.current?.blockedWords).length === 0
          }
          const groups = wallEmojiGroupsOf(envelope, accepted, lane.chunks, address)
          if (groups == null || groups.length === 0) return
          lane.queue = enqueueWallEmoji(lane.queue, groups)
          lane.gapMs = spreadGapMs(lane.queue.length)
          if (lane.timer == null) pump()
        },
        error: (err: unknown) => {
          log({ ytLane: 'subscription error', err: err instanceof Error ? err.name : 'unknown' })
        },
        complete: () => { }
      }
    )
    return () => {
      unsubscribe()
      client.dispose()
    }
  }, [previewOn, youtubeEmojiChannel, token, youtubeChannelsKey])

  const durationSeconds = bootstrap === null ? null : bootstrap.config.durationSeconds
  useEffect(() => {
    if (durationSeconds === null) return
    const durationMs = durationSeconds * 1000
    const interval = globalThis.setInterval(() => {
      const cutoff = Date.now() - durationMs - 500
      setSprites((prev) => {
        if (prev.length === 0) return prev
        const alive = prev.filter((s) => s.spawnedAt > cutoff)
        return alive.length === prev.length ? prev : alive
      })
    }, SWEEP_INTERVAL_MS)
    return () => globalThis.clearInterval(interval)
  }, [durationSeconds])

  const reducedMotion = usePrefersReducedMotion(previewOn)
  const tabVisible = useDocumentVisible(previewOn)
  const sampleSeed = previewOn ? elementSeed(preview.seed, emoteWallId) : 0
  const showTwitchSamples = wallConfig?.showTwitchEmotes === true
  const hasWallConfig = wallConfig != null
  const sampleWordsKey = previewOn ? JSON.stringify(youtubeListOf(wallConfig?.blockedWords)) : '[]'
  const sampleWords = useMemo((): string[] => JSON.parse(sampleWordsKey), [sampleWordsKey])
  const pool = useMemo((): WallSampleEmote[] => {
    if (!previewOn || !hasWallConfig) return EMPTY_POOL
    return wallSamplePool(
      { showTwitchEmotes: showTwitchSamples, show7tvEmotes: show7tv, showBttvEmotes: showBttv, showFfzEmotes: showFfz, youtube: youtubeWall, blockedWords: sampleWords },
      catalog,
      sampleSeed
    )
  }, [previewOn, hasWallConfig, showTwitchSamples, show7tv, showBttv, showFfz, catalog, sampleSeed, youtubeWall, sampleWords])
  const poolKey = pool.map(wallSampleKeyOf).join('\n')
  const poolRef = useRef<WallSampleEmote[]>(EMPTY_POOL)
  poolRef.current = pool
  const sampleDensity = wallConfig == null || !Number.isFinite(wallConfig.density) ? 0 : Math.max(0, Math.floor(wallConfig.density))
  const sampleLifetimeMs = wallConfig == null || !Number.isFinite(wallConfig.durationSeconds) ? 0 : Math.max(0, wallConfig.durationSeconds * 1000)
  const sampleInterval = wallConfig == null ? 0 : wallSampleInterval(wallConfig.density, wallConfig.durationSeconds)
  const samplesShown = previewOn && !realLatched && pool.length > 0 && sampleDensity > 0
  const spawnerOn = samplesShown && !reducedMotion && tabVisible && sampleInterval > 0 && sampleLifetimeMs > 0
  const [sampleSprites, setSampleSprites] = useState<SampleSprite[]>(EMPTY_SAMPLES)
  useEffect(() => {
    if (!previewOn) return
    if (!spawnerOn) {
      setSampleSprites((prev) => (prev.length === 0 ? prev : EMPTY_SAMPLES))
      return
    }
    const interval = sampleInterval
    const lifetimeMs = sampleLifetimeMs
    const cap = sampleDensity
    const make = (k: number): SampleSprite[] => {
      const p = poolRef.current
      if (p.length === 0) return []
      const slot = wallSampleSlot(k, sampleSeed, p.length)
      const emote = p[slot.poolIndex]
      return [{
        key: `${interval}:${k}`,
        url: emote.url,
        ...(emote.emoji != null && { emoji: emote.emoji }),
        provider: emote.provider,
        leftPct: slot.leftPct,
        topPct: slot.topPct,
        driftPx: slot.driftPx,
        sizeFactor: slot.sizeFactor,
        spawnedAt: k * interval
      }]
    }
    const startedAt = Date.now()
    const backfill = wallSlotsBetween(startedAt - lifetimeMs, startedAt, interval).slice(-cap)
    let lastK = backfill.length > 0 ? backfill[backfill.length - 1] : Math.floor(startedAt / interval)
    const poolKeys = new Set(poolRef.current.map(wallSampleKeyOf))
    setSampleSprites((prev) => {
      const kept = prev.filter((sp) => sp.spawnedAt > startedAt - lifetimeMs - SAMPLE_EXPIRY_GRACE_MS && poolKeys.has(wallSampleKeyOf(sp))).slice(-cap)
      if (kept.length === 0) return backfill.flatMap(make)
      const room = cap - kept.length
      if (room <= 0) return kept
      const prevKeys = new Set(prev.map((sp) => sp.key))
      const fill = backfill.filter((k) => !prevKeys.has(`${interval}:${k}`)).slice(-room).flatMap(make)
      return [...kept, ...fill].sort((a, b) => a.spawnedAt - b.spawnedAt)
    })
    let timer: ReturnType<typeof globalThis.setTimeout> | undefined
    const tick = (): void => {
      const now = Date.now()
      const due = wallSlotsBetween(lastK * interval, now, interval)
      if (due.length > 0) lastK = due[due.length - 1]
      const fresh = due.slice(-cap).flatMap(make)
      setSampleSprites((prev) => {
        const alive = prev.filter((sp) => sp.spawnedAt > now - lifetimeMs - SAMPLE_EXPIRY_GRACE_MS)
        const room = cap - alive.length
        if (room <= 0 || fresh.length === 0) return alive.length === prev.length ? prev : alive
        return [...alive, ...fresh.slice(0, room)]
      })
      schedule()
    }
    const schedule = (): void => {
      const now = Date.now()
      timer = globalThis.setTimeout(tick, Math.max(1, (Math.floor(now / interval) + 1) * interval - now))
    }
    schedule()
    return () => globalThis.clearTimeout(timer)
  }, [previewOn, spawnerOn, sampleInterval, sampleLifetimeMs, sampleDensity, sampleSeed, poolKey])
  const staticSamples = useMemo(() => {
    if (!samplesShown || !reducedMotion) return []
    const n = Math.min(sampleDensity, WALL_STATIC_SPRITES)
    return Array.from({ length: n }, (_, k) => {
      const slot = wallSampleSlot(k, sampleSeed, pool.length)
      return { k, url: pool[slot.poolIndex].url, emoji: pool[slot.poolIndex].emoji, slot }
    })
  }, [samplesShown, reducedMotion, sampleDensity, sampleSeed, pool])

  const containerStyle = fillHost ? FILL_HOST_CONTAINER_STYLE : CONTAINER_STYLE

  if (wallConfig == null) {
    return <div ref={hostRef} style={containerStyle} />
  }

  const config = wallConfig
  const sampleStyleKey = config.style === 'POP' ? 'POP' : 'RAIN'
  return (
    <div ref={hostRef} style={containerStyle}>
      <style>{KEYFRAMES_CSS}</style>
      {sprites.map((sprite) => {
        const placed = spriteStyle(sprite, config.style, config.durationSeconds, fillHost ? hostHeight : null)
        if (sprite.emoji != null) {
          return <span key={sprite.key} aria-hidden='true' style={emojiSpriteStyle(placed, sprite.heightPx)}>{sprite.emoji}</span>
        }
        return <img key={sprite.key} src={sprite.url} alt='' style={placed} />
      })}
      {youtubeMarkOn && <YouTubeWallMark />}
      {samplesShown && (
        <div data-testid='overlay-sample-sprites' style={SAMPLE_LAYER_STYLE}>
          {reducedMotion
            ? staticSamples.map((s) => (
              s.emoji != null
                ? <span key={`static:${s.k}`} aria-hidden='true' style={emojiSpriteStyle(staticSampleStyle(s.slot, config.emoteSize), sampleHeightPx(config.emoteSize, s.slot.sizeFactor))}>{s.emoji}</span>
                : <img key={`static:${s.k}`} src={s.url} alt='' style={staticSampleStyle(s.slot, config.emoteSize)} />
            ))
            : sampleSprites.map((sprite) => (
              <SampleSpriteImg
                key={`${sprite.key}:${sampleStyleKey}`}
                sprite={sprite}
                style={config.style}
                durationSeconds={config.durationSeconds}
                emoteSize={config.emoteSize}
                hostHeightPx={fillHost ? hostHeight : null}
              />
            ))}
          {youtubeWall && !youtubeMarkOn && <YouTubeWallMark />}
        </div>
      )}
      {samplesShown && (
        <span data-testid='overlay-sample-tag' style={sampleTagStyle(wallSampleTagBasePx(config.emoteSize))}>Sample</span>
      )}
    </div>
  )
}
